import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  AdminApi,
  AdminRedemptionsListOrderingEnum,
  AdminRedemptionsValuesRetrieveFieldEnum,
  ExternalRedemptionStatusEnum,
  RedemptionList,
} from 'src/api';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import WithDataRecord from 'src/hoc/WithDataRecord';
import Translate from 'src/ui/atoms/translate';
import StatusTag from 'src/ui/atoms/status-tag';
import Date from 'ui/atoms/date';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { Search } from 'src/ui/molecules/filtering';

export interface RedemptionsTableProps {
  forceDataLoad?: boolean;
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const RedemptionsTable: FunctionComponent<RedemptionsTableProps> = WithDataRecord<AdminApi, RedemptionList>(AdminApi)(
  ({ setFilterPopupHeight, data, paginationProps, hasPermission, loading, onOrderBy, ordering }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const getStatusVariant = useCallback((status: ExternalRedemptionStatusEnum | undefined) => {
      switch (status) {
        case ExternalRedemptionStatusEnum.SUCCESS:
          return 'success';
        case ExternalRedemptionStatusEnum.PROCESSING:
          return 'warning';
        case ExternalRedemptionStatusEnum.FAILED:
          return 'danger';
        default:
          return 'info';
      }
    }, []);

    const redemptionStatusFilterOptions = useMemo(() => {
      return Object.values(ExternalRedemptionStatusEnum).map((status) => ({
        id: status,
        name: translate(`studioRedemptionsTable.${snakeToCamelCaseWithNumbers(status)}`),
      }));
    }, [translate]);

    const { activeLocale } = useContext(I18NContext);

    return (
      <>
        <DataRecordTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          data={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          setFilterPopupHeight={setFilterPopupHeight}
          searchComponent={
            <Search
              input="search"
              defaultField="search"
              label={translate('filters.labels.redemptions')}
              name="search"
            />
          }
          cells={[
            {
              title: <Translate name={`studioRedemptionsTable.productName`} />,
              render: ({ productName }) => productName,
              orderingFieldName: AdminRedemptionsListOrderingEnum.PRODUCT_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'productName',
              searchFieldName: AdminRedemptionsValuesRetrieveFieldEnum.PRODUCT_NAME,
              getFilterData: (params: any) => adminApi.adminRedemptionsValuesRetrieve(params),
            },
            {
              title: <Translate name={`studioRedemptionsTable.walletAddress`} />,
              render: ({ walletAddress }) => walletAddress,
              orderingFieldName: AdminRedemptionsListOrderingEnum.WALLET_ADDRESS_ASC,
              type: CellTypeEnum.text,
              filterName: 'walletAddress',
              searchFieldName: AdminRedemptionsValuesRetrieveFieldEnum.WALLET_ADDRESS,
              getFilterData: (params: any) => adminApi.adminRedemptionsValuesRetrieve(params),
            },
            {
              title: <Translate name={`studioRedemptionsTable.units`} />,
              render: ({ units }) => units,
              alignContent: 'right',
              orderingFieldName: AdminRedemptionsListOrderingEnum.UNITS_ASC,
              type: CellTypeEnum.number,
              filterName: 'units',
            },
            {
              title: <Translate name={`studioRedemptionsTable.transactionTimestamp`} />,
              render: ({ transactionTimestamp }) => transactionTimestamp && <Date>{transactionTimestamp}</Date>,
              orderingFieldName: AdminRedemptionsListOrderingEnum.TRANSACTION_TIMESTAMP_ASC,
              type: CellTypeEnum.date,
              filterName: 'transactionTimestamp',
            },
            {
              title: <Translate name={`studioRedemptionsTable.status`} />,
              render: ({ status }) => (
                <StatusTag variant={getStatusVariant(status)}>
                  <Translate name={`studioRedemptionsTable.${status}`} />
                </StatusTag>
              ),
              width: activeLocale === 'de' ? 150 : 132,
              orderingFieldName: AdminRedemptionsListOrderingEnum.STATUS_ASC,
              filterName: 'status',
              getFilterData: () => {
                return redemptionStatusFilterOptions;
              },
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminRedemptionsList({
      offset,
      limit,
      ordering: ordering as AdminRedemptionsListOrderingEnum,
      ...props,
    });
  },
);

export default RedemptionsTable;
